"use client"

import { useState, useEffect, useMemo } from "react"
import { Link } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Checkbox } from "@/components/ui/checkbox"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Textarea } from "@/components/ui/textarea"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {
  Search,
  MoreHorizontal,
  Eye,
  Trash2,
  CheckCircle,
  XCircle,
  MessageCircle,
  Filter,
  AlertTriangle,
  Loader2,
  AlertCircle,
} from "lucide-react"
import { useAppDispatch, useAppSelector } from "@/store/hooks"
import {
  getAllComments,
  bulkModerateComments,
  getBlogById,
  createComment,
  selectComments,
  selectCommentsLoading,
  selectCommentsError,
  selectModeratingComments,
} from "@/store/slices/blogSlice"
import { normalizeCommentForAdmin, flattenCommentsWithReplies } from "@/utils/helper/blog"
import Loader from "@/components/loader/loader"

const statusColors = {
  approved: "bg-green-100 text-green-800",
  pending: "bg-yellow-100 text-yellow-800",
  spam: "bg-red-100 text-red-800",
  rejected: "bg-gray-100 text-gray-800",
}

const statusIcons = {
  approved: CheckCircle,
  pending: AlertTriangle,
  spam: XCircle,
  rejected: XCircle,
}

// Map frontend status to backend action
const statusToAction = {
  approved: "approve",
  pending: "disapprove",
  spam: "delete",
  rejected: "disapprove",
}

export default function AdminBlogComments() {
  const dispatch = useAppDispatch()
  const commentsData = useAppSelector(selectComments)
  const loading = useAppSelector(selectCommentsLoading)
  const error = useAppSelector(selectCommentsError)
  const isBulkModerating = useAppSelector(selectModeratingComments)
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState("all")
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedComment, setSelectedComment] = useState(null)
  const [replyText, setReplyText] = useState("")
  const [selectedComments, setSelectedComments] = useState([])
  const [moderatingComment, setModeratingComment] = useState(null)
  const [blogSlugs, setBlogSlugs] = useState({}) // Cache blog slugs by blogId
  const commentsPerPage = 20

  // Flatten and normalize comments for display
  const comments = useMemo(() => {
    if (!commentsData || commentsData.length === 0) return []
    const flattenedComments = flattenCommentsWithReplies(commentsData)
    return flattenedComments.map(normalizeCommentForAdmin)
  }, [commentsData])

  // Fetch comments from API
  useEffect(() => {
    setCurrentPage(1) // Reset to first page when filter changes
    setSelectedComments([]) // Clear selected comments when filter changes
    
    // Map frontend status filter to backend query params
    const params = {
      limit: 100, // Fetch more comments to handle client-side pagination
      offset: 0,
    }

    // Map status filter to backend approved/deleted params
    if (statusFilter === "approved") {
      params.approved = 1
      params.deleted = 0
    } else if (statusFilter === "pending") {
      params.approved = 0
      params.deleted = 0
    } else if (statusFilter === "spam") {
      params.deleted = 1
    }
    // "all" and "rejected" don't need specific params (all = default, rejected = approved=0)

    dispatch(getAllComments(params))
  }, [statusFilter, dispatch])

  // Fetch blog slugs when comments change
  useEffect(() => {
    if (comments.length > 0) {
      const blogIds = [...new Set(comments.map(c => c.blogId).filter(Boolean))]
      fetchBlogSlugs(blogIds)
    }
  }, [comments])

  // Fetch blog slugs for comments
  const fetchBlogSlugs = async (blogIds) => {
    const slugs = { ...blogSlugs }
    
    // Only fetch slugs for blogs we don't have yet
    const idsToFetch = blogIds.filter(id => !slugs[id])
    
    if (idsToFetch.length === 0) {
      setBlogSlugs(slugs)
      return
    }

    try {
      // Fetch blog data for each blogId to get the slug
      const fetchPromises = idsToFetch.map(async (blogId) => {
        try {
          const result = await dispatch(getBlogById(blogId)).unwrap()
          return { blogId, slug: result.slug || "" }
        } catch (err) {
          console.error(`Error fetching blog ${blogId}:`, err)
          return { blogId, slug: "" }
        }
      })

      const results = await Promise.all(fetchPromises)
      results.forEach(({ blogId, slug }) => {
        if (slug) {
          slugs[blogId] = slug
        }
      })

      setBlogSlugs(slugs)
    } catch (err) {
      console.error("Error fetching blog slugs:", err)
    }
  }

  // Filter comments based on search (status filtering is done on backend)
  const filteredComments = comments.filter((comment) => {
    const matchesSearch =
      comment.author.toLowerCase().includes(searchTerm.toLowerCase()) ||
      comment.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
      comment.blogPost.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (comment.email && comment.email.toLowerCase().includes(searchTerm.toLowerCase()))

    return matchesSearch
  })

  // Pagination
  const totalPages = Math.ceil(filteredComments.length / commentsPerPage)
  const startIndex = (currentPage - 1) * commentsPerPage
  const paginatedComments = filteredComments.slice(startIndex, startIndex + commentsPerPage)

  const handleStatusChange = async (commentId, newStatus) => {
    setModeratingComment(commentId)
    try {
      // Map frontend status to backend action
      const action = statusToAction[newStatus] || "approve"
      
      await dispatch(bulkModerateComments({ commentIds: [commentId], action })).unwrap()
      
      // Comments will be refetched automatically by the thunk
    } catch (err) {
      console.error("Error updating comment status:", err)
      // Error already handled in thunk
    } finally {
      setModeratingComment(null)
    }
  }

  const handleDeleteComment = async (commentId) => {
    setModeratingComment(commentId)
    try {
      await dispatch(bulkModerateComments({ commentIds: [commentId], action: "delete" })).unwrap()
      // Comments will be refetched automatically by the thunk
    } catch (err) {
      console.error("Error deleting comment:", err)
      // Error already handled in thunk
    } finally {
      setModeratingComment(null)
    }
  }

  const handleSelectComment = (commentId, checked) => {
    if (checked) {
      setSelectedComments([...selectedComments, commentId])
    } else {
      setSelectedComments(selectedComments.filter(id => id !== commentId))
    }
  }

  const handleSelectAll = (checked) => {
    if (checked) {
      const pageCommentIds = paginatedComments.map(c => c.id)
      setSelectedComments([...new Set([...selectedComments, ...pageCommentIds])])
    } else {
      const pageCommentIds = paginatedComments.map(c => c.id)
      setSelectedComments(selectedComments.filter(id => !pageCommentIds.includes(id)))
    }
  }

  const handleBulkAction = async (action) => {
    if (selectedComments.length === 0) {
      return
    }

    try {
      await dispatch(bulkModerateComments({ commentIds: selectedComments, action })).unwrap()
      setSelectedComments([])
      // Comments will be refetched automatically by the thunk
    } catch (err) {
      console.error("Error performing bulk action:", err)
      // Error already handled in thunk
    }
  }

  const handleReply = async () => {
    if (!replyText.trim() || !selectedComment) return

    try {
      await dispatch(createComment({
        blogId: selectedComment.blogId,
        commentData: {
          comment: replyText,
          parentId: selectedComment.id,
        },
      })).unwrap()
      
      setReplyText("")
      setSelectedComment(null)
      
      // Refetch comments to show the new reply
      const params = {
        limit: 100,
        offset: 0,
      }
      if (statusFilter === "approved") {
        params.approved = 1
        params.deleted = 0
      } else if (statusFilter === "pending") {
        params.approved = 0
        params.deleted = 0
      } else if (statusFilter === "spam") {
        params.deleted = 1
      }
      dispatch(getAllComments(params))
    } catch (err) {
      console.error("Error sending reply:", err)
      // Error already handled in thunk
    }
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  }

  const getStatusStats = () => {
    return {
      total: comments.length,
      approved: comments.filter((c) => c.status === "approved").length,
      pending: comments.filter((c) => c.status === "pending").length,
      spam: comments.filter((c) => c.status === "spam").length,
    }
  }

  const stats = getStatusStats()

  // Get blog slug for a comment
  const getBlogSlug = (comment) => {
    if (comment.blogSlug) {
      return comment.blogSlug
    }
    if (comment.blogId && blogSlugs[comment.blogId]) {
      return blogSlugs[comment.blogId]
    }
    // Fallback: generate slug from blog title
    return comment.blogPost
      ? comment.blogPost
          .toLowerCase()
          .replace(/[^a-z0-9\s-]/g, "")
          .replace(/\s+/g, "-")
          .replace(/-+/g, "-")
          .trim()
      : ""
  }

  // Loading state
  if (loading) {
    return (
      <div className="w-full max-w-full overflow-hidden flex items-center justify-center min-h-[400px]">
        <Loader />
      </div>
    )
  }

  // Error state
  if (error && comments.length === 0) {
    return (
      <div className="w-full max-w-full overflow-hidden">
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col items-center gap-4 py-8">
              <AlertCircle className="h-12 w-12 text-destructive" />
              <div className="text-center">
                <h3 className="text-lg font-semibold mb-2">Error Loading Comments</h3>
                <p className="text-muted-foreground mb-4">{error}</p>
                <Button onClick={() => {
                  const params = {
                    limit: 100,
                    offset: 0,
                  }
                  if (statusFilter === "approved") {
                    params.approved = 1
                    params.deleted = 0
                  } else if (statusFilter === "pending") {
                    params.approved = 0
                    params.deleted = 0
                  } else if (statusFilter === "spam") {
                    params.deleted = 1
                  }
                  dispatch(getAllComments(params))
                }}>Try Again</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="w-full max-w-full overflow-hidden">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="min-w-0 flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold truncate">Blog Comments</h1>
            <p className="text-muted-foreground text-sm sm:text-base">Manage and moderate blog comments</p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Comments</CardTitle>
              <MessageCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Approved</CardTitle>
              <CheckCircle className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{stats.approved}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pending</CardTitle>
              <AlertTriangle className="h-4 w-4 text-yellow-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-yellow-600">{stats.pending}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Spam</CardTitle>
              <XCircle className="h-4 w-4 text-red-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">{stats.spam}</div>
            </CardContent>
          </Card>
        </div>

        {/* Filters and Search */}
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-4">
              <div className="w-full">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    placeholder="Search by author, content, or blog post..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 w-full"
                  />
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full sm:w-[180px]">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="spam">Spam/Deleted</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bulk Actions */}
        {selectedComments.length > 0 && (
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <span className="text-sm text-muted-foreground">
                  {selectedComments.length} comment(s) selected
                </span>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleBulkAction("approve")}
                    disabled={isBulkModerating}
                  >
                    {isBulkModerating ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="mr-2 h-4 w-4" />
                        Approve
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleBulkAction("disapprove")}
                    disabled={isBulkModerating}
                  >
                    {isBulkModerating ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <XCircle className="mr-2 h-4 w-4" />
                        Disapprove
                      </>
                    )}
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleBulkAction("delete")}
                    disabled={isBulkModerating}
                  >
                    {isBulkModerating ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Comments Cards - Mobile/Tablet View */}
        <div className="xl:hidden space-y-4">
          {paginatedComments.length === 0 ? (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center py-8">
                  <p className="text-muted-foreground">No comments found.</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            paginatedComments.map((comment) => {
              const StatusIcon = statusIcons[comment.status]
              const blogSlug = getBlogSlug(comment)
              const isSelected = selectedComments.includes(comment.id)
              const isModerating = moderatingComment === comment.id
              
              return (
                <Card key={comment.id}>
                  <CardContent className="p-4">
                    <div className="space-y-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center space-x-3 flex-1 min-w-0">
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={(checked) => handleSelectComment(comment.id, checked)}
                            className="flex-shrink-0"
                          />
                          <Avatar className="w-10 h-10 flex-shrink-0">
                            <AvatarImage src={comment.avatar || "/placeholder.svg"} />
                            <AvatarFallback>{comment.author.charAt(0)}</AvatarFallback>
                          </Avatar>
                          <div className="min-w-0 flex-1">
                            <div className="font-medium truncate">
                              {comment.author}
                              {comment.isAdmin && (
                                <Badge variant="secondary" className="ml-2 text-xs">Admin</Badge>
                              )}
                            </div>
                            <div className="text-sm text-muted-foreground truncate">{comment.email || "No email"}</div>
                          </div>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0 flex-shrink-0" disabled={isModerating}>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {blogSlug && (
                              <DropdownMenuItem asChild>
                                <Link to={`/blog/${blogSlug}`} target="_blank">
                                  <Eye className="mr-2 h-4 w-4" />
                                  View Post
                                </Link>
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => setSelectedComment(comment)} disabled={isModerating}>
                              <MessageCircle className="mr-2 h-4 w-4" />
                              Reply
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleDeleteComment(comment.id)} 
                              className="text-red-600"
                              disabled={isModerating}
                            >
                              {isModerating ? (
                                <>
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                  Deleting...
                                </>
                              ) : (
                                <>
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Delete
                                </>
                              )}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>

                      <div className="text-sm line-clamp-3">{comment.content}</div>

                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="outline" className="text-xs truncate">
                          {comment.blogPost}
                        </Badge>
                        {comment.parentId && (
                          <Badge variant="secondary" className="text-xs">Reply</Badge>
                        )}
                        <Select 
                          value={comment.status} 
                          onValueChange={(value) => handleStatusChange(comment.id, value)}
                          disabled={isModerating}
                        >
                          <SelectTrigger className="w-[100px] h-7">
                            <SelectValue>
                              {isModerating ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <div className="flex items-center gap-1">
                                  <StatusIcon className="w-3 h-3" />
                                  <Badge className={`${statusColors[comment.status]} text-xs`}>{comment.status}</Badge>
                                </div>
                              )}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="approved">Approved</SelectItem>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="spam">Spam</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                        <div>
                          <span className="font-medium">Date:</span>
                          <div>{formatDate(comment.date)}</div>
                        </div>
                        <div>
                          <span className="font-medium">Replies:</span>
                          <div>{comment.replies || 0}</div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )
            })
          )}
        </div>

        {/* Comments Table - Desktop View */}
        <div className="hidden xl:block">
          <Card>
            <CardContent className="p-0">
              <div className="overflow-auto max-h-[600px] relative">
                <table className="w-full">
                  <thead className="sticky top-0 z-10 bg-card border-b shadow-sm">
                    <tr>
                      <th className="text-left p-4 font-medium bg-card w-12">
                        <Checkbox
                          checked={paginatedComments.length > 0 && paginatedComments.every(c => selectedComments.includes(c.id))}
                          onCheckedChange={handleSelectAll}
                        />
                      </th>
                      <th className="text-left p-4 font-medium bg-card">Comment</th>
                      <th className="text-left p-4 font-medium bg-card">Blog Post</th>
                      <th className="text-left p-4 font-medium bg-card">Status</th>
                      <th className="text-left p-4 font-medium bg-card">Date</th>
                      <th className="text-right p-4 font-medium bg-card">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedComments.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="p-8 text-center text-muted-foreground">
                          No comments found.
                        </td>
                      </tr>
                    ) : (
                      paginatedComments.map((comment) => {
                        const StatusIcon = statusIcons[comment.status]
                        const blogSlug = getBlogSlug(comment)
                        const isSelected = selectedComments.includes(comment.id)
                        const isModerating = moderatingComment === comment.id
                        
                        return (
                          <tr key={comment.id} className="border-b hover:bg-muted/50">
                            <td className="p-4">
                              <Checkbox
                                checked={isSelected}
                                onCheckedChange={(checked) => handleSelectComment(comment.id, checked)}
                              />
                            </td>
                            <td className="p-4">
                              <div className="flex items-center gap-3">
                                <Avatar className="w-10 h-10 flex-shrink-0">
                                  <AvatarImage src={comment.avatar || "/placeholder.svg"} />
                                  <AvatarFallback>{comment.author.charAt(0)}</AvatarFallback>
                                </Avatar>
                                <div className="min-w-0 flex-1">
                                  <div className="font-medium truncate max-w-[200px]">
                                    {comment.author}
                                    {comment.isAdmin && (
                                      <Badge variant="secondary" className="ml-2 text-xs">Admin</Badge>
                                    )}
                                  </div>
                                  <div className="text-sm text-muted-foreground truncate max-w-[200px]">
                                    {comment.email || "No email"}
                                  </div>
                                  <div className="text-sm text-muted-foreground line-clamp-2 max-w-[300px] mt-1">
                                    {comment.content}
                                  </div>
                                  {comment.parentId && (
                                    <Badge variant="secondary" className="text-xs mt-1">Reply</Badge>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="p-4">
                              <Badge variant="outline" className="text-xs truncate max-w-[150px]">
                                {comment.blogPost}
                              </Badge>
                            </td>
                            <td className="p-4">
                              <Select 
                                value={comment.status} 
                                onValueChange={(value) => handleStatusChange(comment.id, value)}
                                disabled={isModerating}
                              >
                                <SelectTrigger className="w-[120px] h-8">
                                  <SelectValue>
                                    {isModerating ? (
                                      <Loader2 className="h-3 w-3 animate-spin" />
                                    ) : (
                                      <Badge className={`${statusColors[comment.status]} text-xs`}>
                                        {comment.status}
                                      </Badge>
                                    )}
                                  </SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="approved">Approved</SelectItem>
                                  <SelectItem value="pending">Pending</SelectItem>
                                  <SelectItem value="spam">Spam</SelectItem>
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="p-4 text-sm">{formatDate(comment.date)}</td>
                            <td className="p-4">
                              <div className="flex items-center justify-end gap-2">
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" className="h-8 w-8 p-0" disabled={isModerating}>
                                      <MoreHorizontal className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    {blogSlug && (
                                      <DropdownMenuItem asChild>
                                        <Link to={`/blog/${blogSlug}`} target="_blank">
                                          <Eye className="mr-2 h-4 w-4" />
                                          View Post
                                        </Link>
                                      </DropdownMenuItem>
                                    )}
                                    <DropdownMenuItem onClick={() => setSelectedComment(comment)} disabled={isModerating}>
                                      <MessageCircle className="mr-2 h-4 w-4" />
                                      Reply
                                    </DropdownMenuItem>
                                    <DropdownMenuItem 
                                      onClick={() => handleDeleteComment(comment.id)} 
                                      className="text-red-600"
                                      disabled={isModerating}
                                    >
                                      {isModerating ? (
                                        <>
                                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                          Deleting...
                                        </>
                                      ) : (
                                        <>
                                          <Trash2 className="mr-2 h-4 w-4" />
                                          Delete
                                        </>
                                      )}
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            </td>
                          </tr>
                        )
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-sm text-muted-foreground">
              Showing {startIndex + 1} to {Math.min(startIndex + commentsPerPage, filteredComments.length)} of{" "}
              {filteredComments.length} comments
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let page
                  if (totalPages <= 5) {
                    page = i + 1
                  } else if (currentPage <= 3) {
                    page = i + 1
                  } else if (currentPage >= totalPages - 2) {
                    page = totalPages - 4 + i
                  } else {
                    page = currentPage - 2 + i
                  }
                  return (
                    <Button
                      key={page}
                      variant={currentPage === page ? "default" : "outline"}
                      size="sm"
                      onClick={() => setCurrentPage(page)}
                      className="w-8 h-8 p-0"
                    >
                      {page}
                    </Button>
                  )
                })}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}

        {/* Reply Dialog */}
        <Dialog open={!!selectedComment} onOpenChange={(open) => {
          if (!open) {
            setSelectedComment(null)
            setReplyText("")
          }
        }}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Reply to Comment</DialogTitle>
              <DialogDescription>
                Send a reply to this comment. Your reply will be posted as an admin comment.
              </DialogDescription>
            </DialogHeader>
            {selectedComment && (
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center space-x-3 mb-2">
                    <Avatar className="w-8 h-8">
                      <AvatarImage src={selectedComment.avatar || "/placeholder.svg"} />
                      <AvatarFallback>{selectedComment.author.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="font-medium">{selectedComment.author}</div>
                      <div className="text-sm text-muted-foreground">{selectedComment.email || "No email"}</div>
                    </div>
                  </div>
                  <p className="text-sm">{selectedComment.content}</p>
                </div>
                <div>
                  <Textarea
                    placeholder="Type your reply here..."
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    className="min-h-[100px]"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setSelectedComment(null)
                      setReplyText("")
                    }}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleReply} disabled={!replyText.trim()}>
                    Send Reply
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  )
}
